<template>
  <div class="atlas-wrapper">
    <atlas-controls
      :filter-props="defaultProps"
      :allowed-view-types="allowedViewTypes"
      :selected-cycles="selectedCycles"
      :change-selected-goal-cycles="changeSelectedGoalCycles"
      @string-filter-changed="updateSearchTerm"
    />
    <full-screen-spinner v-if="loading" />
    <atlas-content
      v-else
      :network="network"
      :goal-cycles="goalCycles"
      :grouping="currentView.params.grouping"
      :selected-cycles="selectedCycles"
      :filter="currentView.params.filter"
      :groups="groups"
      :load-more-loading="loadMoreLoading"
      @get-children="retrieveChildren"
      @load-more="loadMore"
    />
    <div
      v-if="partiallyLoading || loadMoreLoading"
      class="_partially-loading"
    >
      <m-card padding-xs>
        <div class="_inner">
          <m-icon
            type="loading"
            class="_icon"
          />
          {{ $t('general.loading') }}...
        </div>
      </m-card>
    </div>
  </div>
</template>

<script>
import AtlasContent from '@/components/goal/atlas/AtlasContent.vue';
import AtlasControls from '@/components/goal/atlas/AtlasControls.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import useDebounce from '@/composables/debounce';
import useGoalCycle from '@/composables/goal-cycle/goal-cycle';
import useGoalProperty from '@/composables/property/goal-property';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import usePersistedGoalCycle from '@/composables/goal-cycle/persisted-goal-cycle';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useRouteAwareViews from '@/composables/saved-views/route-aware-views';
import useSpaces from '@/composables/space/spaces';
import { AND, COUNT, RESULT } from 'shared/api/query/constants';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { VIEWS_SERVICE } from '@/lib/constants';
import { applyBaseFilter } from '@/lib/filter/base-filter';
import { atlasList, goalsById } from '@/api/query/nebula/goal';
import { computed, provide } from 'vue';
import { createPropsList } from '@/lib/props';
import { directProperties } from '@/lib/goal/properties';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { logCatch } from '@/lib/logger/logger';
import { mapActions } from 'vuex';
import { optionColor, propertyType, viewType } from 'shared/constants.json';
import { rgbaToHex } from 'shared/lib/color';
import { savedViewProps } from '@/lib/saved-view/props';
import { uniqBy } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { visifyGoals } from '@/lib/goal/visify-goals';

export default {
  name: 'AtlasWrapper',
  props: { ...savedViewProps },
  setup(props) {
    const { t } = useI18n();
    const { goalCycles } = useGoalCycle();
    const { properties: goalProperties } = useGoalProperty();
    const { goalSettings } = useGoalSettings();
    const { userLang } = useLoggedInUser();

    const { composedSelectedCycles, changeSelectedGoalCycles } = usePersistedGoalCycle();

    const { selectSingle, selectMultiple, getGoals } = useGoals();
    const defaultProps = computed(() => createPropsList({
      properties: goalProperties.value,
      directProperties: directProperties((key) => t(key), goalCycles.value),
      userLang: userLang.value,
      model: goalConfig.model,
    }));

    const { debounce } = useDebounce();

    const routeAwareViewsSvc = useRouteAwareViews({
      defaultProps,
      defaultView: props.defaultView,
      application: props.viewApplication,
      readOnly: props.readOnly,
    });

    const { publishedAtFilter } = usePublishedAtFilter();

    provide(VIEWS_SERVICE, routeAwareViewsSvc);
    const { loggedInUserAccount } = useLoggedInUserAccount();

    const { spaces } = useSpaces();

    return {
      account: loggedInUserAccount,
      goalProperties,
      goalSettings,
      userLang,
      defaultProps,

      goalCycles,
      spaces,

      selectSingle,
      selectMultiple,
      selectedCycles: composedSelectedCycles,
      changeSelectedGoalCycles,

      debounce,
      getGoals,

      currentView: routeAwareViewsSvc.currentView,
      initCurrentView: routeAwareViewsSvc.initCurrentView,

      publishedAtFilter,
    };
  },
  components: { AtlasContent, AtlasControls, FullScreenSpinner },
  data() {
    return {
      countAlias: COUNT,
      localGoals: [],
      allowedViewTypes: [viewType.cascade, viewType.list, viewType.atlas],
      searchTerm: '',
      itemsPerPage: 15,
      page: 1,
      goalCount: 0,
      loading: true,
      loadMoreLoading: false,
      partiallyLoading: false,
    };
  },
  computed: {
    groups() {
      return {
        ...this.goalProperties.find((property) => property.type === propertyType.status).options.reduce((res, cur) => ({
          ...res,
          [Number(cur.uid).toString()]: {
            color: this.propertyOptionToGroup(cur),
            borderWidth: 1,
          },
        }), {}),
        company: {
          color: {
            background: this.$colors.shades.white,
            border: rgbaToHex(this.$colors.grey.lighten2),
            highlight: {
              background: this.$colors.shades.white,
              border: rgbaToHex(this.$colors.grey.lighten1),
            },
          },
          borderWidth: 2,
        },
        [optionColor.green]: {
          color: {
            background: this.$colors.green.lighten2,
            border: this.$colors.green.lighten2,
            highlight: {
              background: this.$colors.green.lighten1,
              border: this.$colors.green.lighten1,
            },
          },
          borderWidth: 1,
        },
        [optionColor.yellow]: {
          color: {
            background: this.$colors.yellow.lighten2,
            border: this.$colors.yellow.lighten2,
            highlight: {
              background: this.$colors.yellow.lighten1,
              border: this.$colors.yellow.lighten1,
            },
          },
          borderWidth: 1,
        },
        [optionColor.red]: {
          color: {
            background: this.$colors.red.lighten2,
            border: this.$colors.red.lighten2,
            highlight: {
              background: this.$colors.red.lighten1,
              border: this.$colors.red.lighten1,
            },
          },
          borderWidth: 1,
        },
        [optionColor.grey]: {
          color: {
            background: this.$colors.grey.lighten2,
            border: this.$colors.grey.lighten2,
            highlight: {
              background: this.$colors.grey.lighten1,
              border: this.$colors.grey.lighten1,
            },
          },
          borderWidth: 1,
        },
      };
    },
    filter() {
      return this.currentView.params.filter;
    },
    props() {
      return this.currentView.params.props;
    },
    displayableProps() {
      return this.currentView.params.props.filter((p) => !p.isDirect && [propertyType.options, propertyType.space].includes(p.property.type));
    },
    network() {
      return visifyGoals(
        this.selectMultiple(this.localGoals.map((g) => g.uid)),
        this.account,
        this.currentView.params.grouping,
        this.goalSettings,
        this.displayableProps,
        this.spaces,
        this.userLang,
      );
    },
    gqlFilterObject() {
      const handler = new GoalFilterHandler();
      return handler.Translate(this.currentView.params.filter);
    },
    gqlFilter() {
      return this.gqlFilterObject.filterTree;
    },
  },
  methods: {
    ...mapActions([
      'getEntities',
    ]),
    loadMore() {
      this.page += 1;
      this.loadMoreLoading = true;
      this.retrieveGoals(false);
    },
    updateSearchTerm(term) {
      this.searchTerm = term;
    },
    apply(wait) {
      this.debounce(this.retrieveGoals, wait);
    },
    retrieveGoals(setLoading = true) {
      if (setLoading) {
        this.loading = true;
        this.page = 1;
      }
      this.getGoals({
        queries: applyBaseFilter({
          queries: atlasList({
            pagination: {
              page: this.page,
              itemsPerPage: this.itemsPerPage,
              countAlias: this.countAlias,
            },
            goalCycles: this.selectedCycles,
            searchTerm: this.searchTerm,
            filter: this.gqlFilterObject.filterTree,
            varBlocks: this.gqlFilterObject.varBlocks,
            childrenPagination: { itemsPerPage: 30, page: 1 },
          }),
          operator: AND,
          baseFilter: this.publishedAtFilter,
        }),
        model: goalConfig.model,
        countAlias: this.countAlias,
      }).then((response) => {
        this.loading = false;
        this.loadMoreLoading = false;
        if (setLoading) {
          this.localGoals = response.all;
          this.goalCount = response.count[0].count;
          return;
        }
        this.localGoals.push(...response.all);
        this.goalCount += response.count[0].count;
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));
    },
    retrieveChildren(id) {
      const goal = this.selectSingle(id);
      if (goal === null) {
        return;
      }
      if (typeof goal.children === 'undefined') {
        return;
      }
      const ids = goal.children.map((g) => g.uid);
      if (ids.length === 0) {
        return;
      }
      this.partiallyLoading = true;
      this.getGoals({
        queries: goalsById({
          ids,
          alias: RESULT,
          pagination: {
            page: 1,
            itemsPerPage: 100,
            countAlias: COUNT,
          },
        }),
        countKey: 'childrenCount',
      }).then((response) => {
        this.partiallyLoading = false;
        this.localGoals = uniqBy([...this.localGoals, ...response.result], 'uid');
      }).catch(logCatch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      }));
    },
    propertyOptionToGroup(propertyOption) {
      const colorTranslator = (color) => {
        if (color.startsWith('#')) {
          return color;
        }

        return rgbaToHex(color);
      };

      const buildTranslation = (color) => ({
        background: colorTranslator(color.lighten2),
        border: colorTranslator(color.lighten2),
        highlight: {
          background: colorTranslator(color.lighten1),
          border: colorTranslator(color.lighten1),
        },
      });

      if (this.$colors[propertyOption.color] !== undefined) {
        return buildTranslation(this.$colors[propertyOption.color]);
      }

      return buildTranslation(this.$colors.grey);
    },
  },
  watch: {
    searchTerm(val, oldVal) {
      if (val === oldVal) {
        return;
      }
      this.apply(1000);
    },
    selectedCycles(val, oldVal) {
      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }
      this.apply(1000);
    },
    gqlFilterObject: {
      handler(val, newVal) {
        if (JSON.stringify(val) === JSON.stringify(newVal)) {
          return;
        }
        this.apply(1000);
      },
      deep: true,
    },
  },
  mounted() {
    this.initCurrentView();
    this.apply(0);
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  .atlas-wrapper {
    ._partially-loading {
      position: fixed;
      right: 0;
      bottom: 2rem;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto;
      animation: enter .1s ease-in-out;

      @keyframes enter {
        0% {
          bottom: -2rem;
        }

        100% {
          bottom: 2rem;
        }
      }

      ._inner {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      ._icon {
        margin-right: .6rem;
      }
    }
  }
</style>
